import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [socket, setSocket] = useState(null); // ← State for socket to trigger re-renders
  const socketRef = useRef(null);

  // Set mounted state
  useEffect(() => {
    setMounted(true);
  }, []);

  // Initialize or reinitialize socket based on token
  const initializeSocket = (token) => {
    console.log('[WebSocketContext] Initializing socket with token');
    
    // Disconnect existing socket if any
    if (socketRef.current) {
      console.log('[WebSocketContext] Disconnecting existing socket');
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Create new socket
    const newSocket = io(process.env.REACT_APP_WS_URL || `http://${window.location.hostname}:5000`, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    
    socketRef.current = newSocket;
    setSocket(newSocket);

    // Connection events
    newSocket.on('connect', () => {
      console.log('[WS] Connected:', newSocket.id);
      setConnected(true);
      setError(null);
    });

    newSocket.on('connection:success', (data) => {
      console.log('[WS] Connection success:', data);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[WS] Disconnected:', reason);
      setConnected(false);
    });

    newSocket.on('connect_error', (err) => {
      console.error('[WS] Connection error:', err.message);
      setError(err.message);
      setConnected(false);
    });

    newSocket.on('error', (err) => {
      console.error('[WS] Error:', err);
      setError(err.message || 'WebSocket error');
    });
  };

  // Initial socket setup
  useEffect(() => {
    if (!mounted) return;
    
    console.log('[WebSocketContext] Checking for initial token');
    const token = localStorage.getItem('token');
    
    if (token) {
      console.log('[WebSocketContext] Token found, initializing socket');
      initializeSocket(token);
    } else {
      console.log('[WebSocketContext] No token found, waiting for login');
    }
    
    // Cleanup
    return () => {
      if (socketRef.current) {
        console.log('[WebSocketContext] Cleaning up socket on unmount');
        socketRef.current.disconnect();
      }
    };
  }, [mounted]);

  // Listen for token changes (login/logout)
  useEffect(() => {
    if (!mounted) return;

    // Handle same-tab changes via custom event
    const handleTokenChange = (e) => {
      console.log('[WebSocketContext] Token changed (custom event):', e.detail.action);
      const newToken = e.detail.token;
      
      if (e.detail.action === 'login' && newToken) {
        console.log('[WebSocketContext] Login detected, connecting socket');
        initializeSocket(newToken);
      } else if (e.detail.action === 'logout') {
        console.log('[WebSocketContext] Logout detected, disconnecting socket');
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
          setSocket(null);
          setConnected(false);
        }
      }
    };

    // Handle cross-tab changes via storage event
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        console.log('[WebSocketContext] Token changed (storage event)');
        const newToken = e.newValue;
        
        if (newToken && !socketRef.current) {
          console.log('[WebSocketContext] Token added, connecting socket');
          initializeSocket(newToken);
        } else if (!newToken && socketRef.current) {
          console.log('[WebSocketContext] Token removed, disconnecting socket');
          socketRef.current.disconnect();
          socketRef.current = null;
          setSocket(null);
          setConnected(false);
        }
      }
    };

    window.addEventListener('tokenChanged', handleTokenChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('tokenChanged', handleTokenChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [mounted]);

  const value = {
    socket, // ← Use state instead of ref
    connected,
    error
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
};
